import { stegaClean } from '@/lib/utils/stega';
import type { MediaDto } from '@/lib/sanity/dto';
import { AI_EDITED_LABEL } from '@/lib/images/ai-edited.constants';

/**
 * Readable form of the asset's file name — extension dropped, separators turned
 * into spaces ('wohnung-39_bad.jpg' → 'wohnung 39 bad'). Last resort for a name:
 * a file name read aloud is poor, but silence is worse.
 */
function resolveFilenameLabel(asset?: MediaDto['asset']): string {
    const filename = stegaClean(asset?.originalFilename || '');

    return filename
        .replace(/\.[^.]+$/, '')
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Accessible alt text for a Sanity image: asset `altText`, then the media library
 * `title`, then the caller's fallback (which knows the context — a person's name,
 * the block title), and the asset's file name as the last resort. So the result is
 * effectively never empty — set `altText` in Sanity to keep it from degrading to a
 * file name.
 *
 * Purely decorative images must NOT use this helper: pass `alt=""` directly.
 *
 * `alt` is an attribute, not rendered text, so stega encoding is stripped here
 * (see AGENTS.md → Stega encoding).
 */
export function resolveImageAlt(image?: MediaDto | null, fallback = ''): string {
    const asset = image?.asset;
    const authored = stegaClean(asset?.altText || asset?.title || '');

    return authored || stegaClean(fallback) || resolveFilenameLabel(asset);
}

/** Whether the asset is flagged "KI-gestützt bearbeitet" in the media library. */
export function isAiEdited(image?: MediaDto | null): boolean {
    return image?.asset?.aiEdited === true;
}

/**
 * Accessible name for a control whose only content is an image (lightbox
 * triggers). The caption wins over the asset metadata: it describes the image in
 * context, and when it is also rendered inside the control the name still
 * contains the visible text (WCAG 2.5.3 "Label in Name"). The same holds for the
 * AI badge an AI-edited image carries. `fallback` is the last resort behind the
 * whole alt cascade, file name included.
 */
export function resolveImageActionLabel(
    image: MediaDto | undefined | null,
    action: string,
    caption?: string,
    fallback = 'Bild',
): string {
    const cleanCaption = caption ? stegaClean(caption).trim() : '';
    const subject = cleanCaption || resolveImageAlt(image) || fallback;
    const label = `${subject} ${action}`;

    return isAiEdited(image) ? `${label}, ${AI_EDITED_LABEL}` : label;
}

/**
 * Whether the asset is an SVG. Those bypass both image pipelines: the Sanity CDN
 * serves them untransformed, and the Next optimizer rejects them outright unless
 * `dangerouslyAllowSVG` is set — so a caller has to hand the asset URL to the
 * browser as it is (`unoptimized`).
 */
export function isSvgImage(image?: MediaDto | null): boolean {
    const asset = image?.asset;

    return asset?.mimeType === 'image/svg+xml' || asset?.extension === 'svg';
}
